import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, apiPut, apiDelete, apiPost } from '../lib/api'
// @ts-ignore
import type ReactGridLayout from 'react-grid-layout'

// ── Types ────────────────────────────────────────────────────────────

export interface WidgetSettings {
    showPctChange?: boolean
    showNetChange?: boolean
    showUnits?: boolean
    showSKUs?: boolean
    showValue?: boolean
    showTooltipYoY?: boolean
    showTooltipMoM?: boolean
    forceZeroYAxis?: boolean
}

export interface WidgetMeta {
    id: string
    label: string
    description: string
    category: 'KPI' | 'Chart' | 'Table'
    dashboardTypes: string
    defaultW: number
    defaultH: number
    isEnabled: boolean
}

/** Default dimensions for each inventory widget at full size (based on 40 columns) */
export const DEFAULT_WIDGET_DIMS: Record<string, { w: number, h: number }> = {
    invTotalUnits:      { w: 10, h: 10 },
    invUniqueSKUs:      { w: 10, h: 10 },
    invValue:           { w: 10, h: 10 },
    invMoMChange:       { w: 10, h: 10 },
    invLevelChart:      { w: 30, h: 20 },
    invSnapshotSummary: { w: 10, h: 20 },
    invDeptPie:         { w: 20, h: 20 },
    invStockouts:       { w: 40, h: 20 },
}

/** Compute actual dimensions considering dynamic size */
export function getWidgetDims(widgetId: string): { w: number, h: number } {
    let def = DEFAULT_WIDGET_DIMS[widgetId]

    // If not found in defaults, check dynamic widgets from the server
    if (!def) {
        const meta = useInventoryDashboardLayoutStore.getState().availableWidgets?.find(w => w.id === widgetId)
        if (meta) {
            def = { w: Math.round(meta.defaultW * 2.5), h: Math.round(meta.defaultH * 2.5) }
        } else {
            def = { w: 10, h: 10 } // Absolute fallback
        }
    }

    return def
}

export interface DashboardLayoutPreset {
    id: string
    name: string | null
    isDefault: boolean
    sortOrder: number
}

export interface InventoryDashboardLayoutState {
    /** The actual RGL grid properties for each breakpoint */
    layouts: Record<string, ReactGridLayout.Layout[]>
    /** Widget IDs currently on the board */
    activeWidgets: string[]
    /** Per-widget settings */
    widgetSettings: Record<string, WidgetSettings>
    /** Whether edit mode is active */
    isEditing: boolean
    /** Available widgets from the server */
    availableWidgets: WidgetMeta[]
    /** Whether initial load has happened */
    loaded: boolean
    
    /** The active layout preset details */
    availableLayouts: DashboardLayoutPreset[]
    currentLayoutId: string | null
    currentLayoutName: string | null
    currentLayoutIsDefault: boolean
    currentLayoutSource: 'user' | 'system'

    /** The user ID whose layout is stored — safety net to detect missed logouts */
    storedUserId: string | null
    /** Previous layouts for undo snapshot (single move) */
    previousLayouts: Record<string, ReactGridLayout.Layout[]> | null
    /** State captured before entering edit mode for "Undo All" */
    preEditState: {
        layouts: Record<string, ReactGridLayout.Layout[]>
        activeWidgets: string[]
        widgetSettings: Record<string, WidgetSettings>
    } | null
    /** Monotonic counter — incremented on reset to force react-grid-layout remount */
    layoutVersion: number

    /** Call on logout — wipes ALL user state so it never leaks to the next user */
    clearUserState: () => void
    setEditing: (editing: boolean) => void
    addWidget: (id: string) => void
    removeWidget: (id: string) => void
    setLayouts: (layouts: Record<string, ReactGridLayout.Layout[]>) => void
    snapshotLayouts: () => void
    undoLayouts: () => void
    revertToInitial: () => void
    updateWidgetSettings: (id: string, settings: Partial<WidgetSettings>) => void
    resetLayout: () => void
    resetToSystemDefault: (token: string, userId: string, dashboardType?: string) => Promise<void>
    setAvailableWidgets: (widgets: WidgetMeta[]) => void
    loadFromServer: (token: string, userId: string, dashboardType?: string) => Promise<void>
    loadLayout: (token: string, userId: string, dashboardType?: string, id?: string | null) => Promise<void>
    saveToAccount: (token: string, name: string | null, isDefault: boolean, isNew: boolean, dashboardType?: string) => Promise<void>
    saveAsSystemDefault: (token: string, dashboardType?: string, overwriteUsers?: boolean) => Promise<void>
    hasUnsavedChanges: () => boolean
    applyImportedLayout: (parsed: { layouts: Record<string, any[]>; activeWidgets: string[]; widgetSettings: Record<string, WidgetSettings> }) => void
}

// ── Default layout ───────────────────────────────────────────────────

const DEFAULT_ACTIVE_WIDGETS = [
    'invTotalUnits', 'invUniqueSKUs', 'invValue', 'invMoMChange',
    'invLevelChart', 'invSnapshotSummary',
    'invDeptPie',
    'invStockouts',
]

const DEFAULT_WIDGET_SETTINGS: Record<string, WidgetSettings> = {}
DEFAULT_ACTIVE_WIDGETS.forEach(id => {
    DEFAULT_WIDGET_SETTINGS[id] = {}
})

export function generateDefaultLayouts(): Record<string, ReactGridLayout.Layout[]> {
    const lg: ReactGridLayout.Layout[] = []
    const xs: ReactGridLayout.Layout[] = []
    let xl = 0, yl = 0, xx = 0, yx = 0
    DEFAULT_ACTIVE_WIDGETS.forEach((id) => {
        const dims = getWidgetDims(id)
        if (xl + dims.w > 40) { xl = 0; yl += dims.h }
        lg.push({ i: id, x: xl, y: yl, w: dims.w, h: dims.h })
        xl += dims.w

        const xw = dims.w >= 20 ? 10 : 5
        const xh = dims.h >= 20 ? 10 : dims.h
        if (xx + xw > 10) { xx = 0; yx += xh }
        xs.push({ i: id, x: xx, y: yx, w: xw, h: xh })
        xx += xw
    })
    return { lg, xs }
}

// ── Store ────────────────────────────────────────────────────────────

export const useInventoryDashboardLayoutStore = create<InventoryDashboardLayoutState>()(
    persist(
        (set, get) => ({
            layouts: generateDefaultLayouts(),
            activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
            widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
            isEditing: false,
            availableWidgets: [],
            loaded: false,
            availableLayouts: [],
            currentLayoutId: null,
            currentLayoutName: null,
            currentLayoutIsDefault: false,
            currentLayoutSource: 'system',
            storedUserId: null,
            previousLayouts: null,
            preEditState: null,
            layoutVersion: 0,

            // ── Called on logout: wipes ALL user-specific state ─────────────────
            clearUserState: () => set((s) => ({
                layouts: generateDefaultLayouts(),
                activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
                widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
                isEditing: false,
                availableWidgets: [],
                loaded: false,
                availableLayouts: [],
                currentLayoutId: null,
                currentLayoutName: null,
                currentLayoutIsDefault: false,
                currentLayoutSource: 'system',
                storedUserId: null,
                previousLayouts: null,
                preEditState: null,
                layoutVersion: s.layoutVersion + 1,
            })),

            setEditing: (editing) => {
                const state = get()
                if (editing && !state.isEditing) {
                    set({
                        isEditing: true,
                        preEditState: {
                            layouts: JSON.parse(JSON.stringify(state.layouts)),
                            activeWidgets: [...state.activeWidgets],
                            widgetSettings: JSON.parse(JSON.stringify(state.widgetSettings)),
                        }
                    })
                } else if (!editing) {
                    set({ isEditing: false, preEditState: null })
                }
            },

            addWidget: (id) => {
                const state = get()
                if (state.activeWidgets.includes(id)) return

                const lgLayouts = state.layouts.lg || []
                const maxY = lgLayouts.reduce((max, l) => Math.max(max, l.y + l.h), 0)
                const dims = getWidgetDims(id)
                const newLayoutItem = { i: id, x: 0, y: maxY, w: dims.w, h: dims.h }

                const xsLayouts = state.layouts.xs || []
                const maxYxs = xsLayouts.reduce((max, l) => Math.max(max, l.y + l.h), 0)
                const xw = dims.w >= 20 ? 10 : 5
                const xh = dims.h >= 20 ? 10 : Math.max(5, Math.ceil(dims.h / 2))
                const newXsItem = { i: id, x: 0, y: maxYxs, w: xw, h: xh }

                set({
                    activeWidgets: [...state.activeWidgets, id],
                    layouts: {
                        ...state.layouts,
                        lg: [...lgLayouts, newLayoutItem],
                        xs: [...xsLayouts, newXsItem]
                    },
                    widgetSettings: {
                        ...state.widgetSettings,
                        [id]: {},
                    },
                })
            },

            removeWidget: (id) => {
                const state = get()
                set({
                    activeWidgets: state.activeWidgets.filter(w => w !== id),
                    layouts: {
                        ...state.layouts,
                        lg: (state.layouts.lg || []).filter(l => l.i !== id),
                        xs: (state.layouts.xs || []).filter(l => l.i !== id)
                    }
                })
            },

            setLayouts: (layouts) => set({ layouts }),

            snapshotLayouts: () => {
                const state = get()
                set({ previousLayouts: JSON.parse(JSON.stringify(state.layouts)) })
            },

            undoLayouts: () => {
                const state = get()
                if (state.previousLayouts) {
                    set({ layouts: state.previousLayouts, previousLayouts: null })
                }
            },

            revertToInitial: () => {
                const state = get()
                if (state.preEditState) {
                    set({
                        layouts: state.preEditState.layouts,
                        activeWidgets: state.preEditState.activeWidgets,
                        widgetSettings: state.preEditState.widgetSettings,
                        isEditing: false,
                        preEditState: null,
                    })
                }
            },

            updateWidgetSettings: (id, settings) => {
                const state = get()
                set({
                    widgetSettings: {
                        ...state.widgetSettings,
                        [id]: { ...state.widgetSettings[id], ...settings },
                    },
                })
            },

            resetLayout: () => set({
                layouts: generateDefaultLayouts(),
                activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
                widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
                previousLayouts: null,
                preEditState: null,
            }),

            resetToSystemDefault: async (token, userId, dashboardType = 'inventory') => {
                try {
                    await apiDelete(`/api/dashboard/layout/all?type=${dashboardType}`, token)
                } catch { /* ignore */ }

                set({ loaded: false, isEditing: false, previousLayouts: null, preEditState: null })
                await get().loadFromServer(token, userId, dashboardType)
                set((s) => ({ layoutVersion: s.layoutVersion + 1 }))
            },

            setAvailableWidgets: (widgets) => set({ availableWidgets: widgets }),

            loadFromServer: async (token, userId, dashboardType = 'inventory') => {
                try {
                    const widgetData = await api(`/api/dashboard/widgets?type=${dashboardType}`, token)
                    if (widgetData?.widgets) {
                        set({ availableWidgets: widgetData.widgets })
                    }

                    const state = get()
                    const userChanged = state.storedUserId !== null && state.storedUserId !== userId
                    if (userChanged) {
                        set({
                            layouts: generateDefaultLayouts(),
                            activeWidgets: [...DEFAULT_ACTIVE_WIDGETS],
                            widgetSettings: { ...DEFAULT_WIDGET_SETTINGS },
                            isEditing: false,
                            loaded: false,
                            availableLayouts: [],
                            currentLayoutId: null,
                            currentLayoutName: null,
                            currentLayoutIsDefault: false,
                            currentLayoutSource: 'system',
                            storedUserId: null,
                            previousLayouts: null,
                            preEditState: null,
                        })
                    }

                    // Load presets
                    try {
                        const layoutRes = await api(`/api/dashboard/layouts?type=${dashboardType}`, token)
                        if (layoutRes?.layouts) {
                            set({ availableLayouts: layoutRes.layouts })
                        }
                    } catch (e) { console.warn('[inventory] Failed to load preset list:', e) }

                    if (!get().loaded) {
                        await get().loadLayout(token, userId, dashboardType, null)
                    }
                } catch (e) {
                    console.warn('[inventory] loadFromServer failed:', e)
                }
            },

            loadLayout: async (token, userId, dashboardType = 'inventory', id = null) => {
                try {
                    let endpoint = `/api/dashboard/layout?type=${dashboardType}`
                    if (id === 'system-default') endpoint += `&systemDefault=true`
                    else if (id) endpoint += `&id=${id}`

                    const layoutData = await api(endpoint, token)
                    if (layoutData?.layoutJson) {
                        try {
                            const parsed = JSON.parse(layoutData.layoutJson)
                            if (parsed.activeWidgets) {
                                set({
                                    layouts: parsed.layouts ?? generateDefaultLayouts(),
                                    activeWidgets: parsed.activeWidgets,
                                    widgetSettings: parsed.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS },
                                    currentLayoutId: layoutData.id ?? null,
                                    currentLayoutName: layoutData.name ?? null,
                                    currentLayoutIsDefault: layoutData.isDefault ?? false,
                                    currentLayoutSource: layoutData.source,
                                    loaded: true,
                                    storedUserId: userId,
                                })
                            }
                        } catch (e) {
                            console.warn('[inventory] Failed to parse server layout JSON:', e)
                        }
                    } else {
                        set({
                            currentLayoutId: null,
                            currentLayoutName: null,
                            currentLayoutIsDefault: false,
                            currentLayoutSource: 'system',
                        })
                    }
                    set((s) => s.loaded ? {} : { loaded: true, storedUserId: userId })
                } catch (e) {
                    console.warn('[inventory] loadLayout failed:', e)
                }
            },

            saveToAccount: async (token, name, isDefault, isNew, dashboardType = 'inventory') => {
                const state = get()
                const layoutJson = JSON.stringify({
                    layouts: state.layouts,
                    activeWidgets: state.activeWidgets,
                    widgetSettings: state.widgetSettings,
                })
                
                try {
                    let res;
                    if (isNew || !state.currentLayoutId || state.currentLayoutSource === 'system') {
                        res = await apiPost('/api/dashboard/layout', token, {
                            layoutJson, dashboardType, name, isDefault
                        })
                    } else {
                        res = await apiPut(`/api/dashboard/layout/${state.currentLayoutId}`, token, {
                            layoutJson, dashboardType, name, isDefault
                        })
                    }

                    const layoutRes = await api(`/api/dashboard/layouts?type=${dashboardType}`, token)
                    if (layoutRes?.layouts) {
                        set({ availableLayouts: layoutRes.layouts })
                    }
                    if (res?.id) {
                        await get().loadLayout(token, state.storedUserId!, dashboardType, res.id)
                    } else {
                        await get().loadLayout(token, state.storedUserId!, dashboardType, state.currentLayoutId)
                    }
                } catch (e) {
                    console.error('[inventory] saveToAccount failed', e)
                }
            },

            saveAsSystemDefault: async (token, dashboardType = 'inventory', overwriteUsers = false) => {
                const state = get()
                const layoutJson = JSON.stringify({
                    layouts: state.layouts,
                    activeWidgets: state.activeWidgets,
                    widgetSettings: state.widgetSettings,
                })
                await apiPut('/api/admin/dashboard/layout/default', token, {
                    layoutJson,
                    dashboardType,
                    overwriteUsers,
                })
            },

            hasUnsavedChanges: () => {
                const state = get()
                if (!state.isEditing || !state.preEditState) return false
                return (
                    JSON.stringify(state.activeWidgets) !== JSON.stringify(state.preEditState.activeWidgets) ||
                    JSON.stringify(state.layouts) !== JSON.stringify(state.preEditState.layouts) ||
                    JSON.stringify(state.widgetSettings) !== JSON.stringify(state.preEditState.widgetSettings)
                )
            },

            applyImportedLayout: (parsed) => {
                set((s) => ({
                    layouts: parsed.layouts ?? generateDefaultLayouts(),
                    activeWidgets: parsed.activeWidgets,
                    widgetSettings: parsed.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS },
                    currentLayoutId: null,
                    currentLayoutName: null,
                    currentLayoutIsDefault: false,
                    currentLayoutSource: 'system',
                    layoutVersion: s.layoutVersion + 1,
                    preEditState: {
                        layouts: JSON.parse(JSON.stringify(parsed.layouts ?? generateDefaultLayouts())),
                        activeWidgets: [...parsed.activeWidgets],
                        widgetSettings: JSON.parse(JSON.stringify(parsed.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS })),
                    },
                }))
            },
        }),
        {
            name: 'puresoft-inventory-dashboard-layout',
            version: 1,
            migrate: (persistedState: any, _version: number) => {
                const state = persistedState as Partial<InventoryDashboardLayoutState>
                return {
                    layouts: state.layouts ?? generateDefaultLayouts(),
                    activeWidgets: state.activeWidgets ?? [...DEFAULT_ACTIVE_WIDGETS],
                    widgetSettings: state.widgetSettings ?? { ...DEFAULT_WIDGET_SETTINGS },
                    loaded: false,
                    storedUserId: state.storedUserId ?? null,
                    previousLayouts: null,
                    preEditState: null,
                }
            },
            partialize: (state) => ({
                layouts: state.layouts,
                activeWidgets: state.activeWidgets,
                widgetSettings: state.widgetSettings,
                storedUserId: state.storedUserId,
                currentLayoutId: state.currentLayoutId,
                currentLayoutName: state.currentLayoutName,
                currentLayoutIsDefault: state.currentLayoutIsDefault,
                currentLayoutSource: state.currentLayoutSource,
            }) as any,
        }
    )
)
